import Graph from 'graphology';
import { dfsFromNode } from 'graphology-traversal/dfs';
import memoize from 'lodash/memoize';
import pickBy from 'lodash/pickBy';
import uniqWith from 'lodash/uniqWith';
import { Connection } from 'reactflow';

import {
  GraphState,
  NodePositionById,
  TimeConstraintInstancesById,
} from './graphSlice';
import type {
  CreateLabwareData,
  CreateTaskData,
  GraphEdges,
  TimeConstraintInstance,
} from '../../types';
import {
  getConnectedEdgeIdsForNodes,
  getConnectedTaskDependencyIdsForNodes,
  getConnectedTimeConstraintIdsForNodes,
  getHandlePairIdConnections,
  parseHandlePairId,
} from '../../utils';

export function deleteEdgeHelper(state: GraphState, id: string): void {
  const timeConstraint = state.timeConstraints[id];
  if (timeConstraint !== undefined) {
    delete state.timeConstraints[id];
    return;
  }

  const dataEdge = state.dataDependencyEdges.find((edge) => edge.id === id);

  if (dataEdge !== undefined) {
    state.dataDependencyEdges = state.dataDependencyEdges.filter(
      (edge) => edge.id !== id
    );
    return;
  }

  // task dependency edges don't need to be handled here as they are derived from task dependencies themselves

  const labwareDependencyEdge = state.labwareDependencyEdges.find(
    (edge) => edge.id === id
  );

  // labware dependency edge need additional work for handle pair handling
  if (labwareDependencyEdge === undefined) return;

  // TODO(IL, 2024-04-04): can optimize since we are doing find above... or just have edges by by ID??
  state.labwareDependencyEdges = state.labwareDependencyEdges.filter(
    (edge) => edge.id !== id
  );

  // TODO(IL, 2024-04-04): DRY the handlePairIdOrderByNode updates
  // remove edge's target handle from target node's handlePairId ordering
  const targetHandlePairId =
    labwareDependencyEdge.targetHandle != null
      ? parseHandlePairId(labwareDependencyEdge.targetHandle)
      : null;
  if (targetHandlePairId != null) {
    const { upstream, downstream } = getHandlePairIdConnections({
      labwareDependencyEdges: state.labwareDependencyEdges,
      nodeId: labwareDependencyEdge.target,
      handlePairId: targetHandlePairId,
    });
    const prevHandles =
      state.handlePairIdOrderByNode[labwareDependencyEdge.target];
    if (prevHandles !== undefined && !upstream && !downstream) {
      state.handlePairIdOrderByNode[labwareDependencyEdge.target] =
        prevHandles.filter(
          (handlePairId) => handlePairId !== targetHandlePairId
        );
    }
  }
  // cleanup: remove {blahKey: []} empty array values
  if (
    state.handlePairIdOrderByNode[labwareDependencyEdge.target]?.length === 0
  ) {
    delete state.handlePairIdOrderByNode[labwareDependencyEdge.target];
  }
  // remove edge's source handle from source node's handlePairId ordering
  const sourceHandlePairId =
    labwareDependencyEdge.sourceHandle != null
      ? parseHandlePairId(labwareDependencyEdge.sourceHandle)
      : null;
  if (sourceHandlePairId != null) {
    const { upstream, downstream } = getHandlePairIdConnections({
      labwareDependencyEdges: state.labwareDependencyEdges,
      nodeId: labwareDependencyEdge.source,
      handlePairId: sourceHandlePairId,
    });
    const prevHandles =
      state.handlePairIdOrderByNode[labwareDependencyEdge.source];
    if (prevHandles !== undefined && !upstream && !downstream) {
      state.handlePairIdOrderByNode[labwareDependencyEdge.source] =
        prevHandles.filter(
          (handlePairId) => handlePairId !== sourceHandlePairId
        );
    }
  }
  // cleanup: remove {blahKey: []} empty array values
  if (
    state.handlePairIdOrderByNode[labwareDependencyEdge.source]?.length === 0
  ) {
    delete state.handlePairIdOrderByNode[labwareDependencyEdge.source];
  }
}

export function deleteNodesHelper(args: {
  state: GraphState;
  labwareIds: string[];
  taskIds: string[];
}) {
  const { state, labwareIds, taskIds } = args;
  // delete labware entities and associated data
  for (const id of labwareIds) {
    delete state.labwareNodes[id];
    delete state.nodeIdToColorIndex[id];

    for (const [taskId, mapping] of Object.entries(
      state.labwareSlotMappingByTask
    )) {
      state.labwareSlotMappingByTask[taskId] = pickBy(
        mapping,
        (_slot, labwareId) => labwareId !== id
      );
    }
  }

  // delete task entities and associated data
  for (const id of taskIds) {
    delete state.taskNodes[id];

    delete state.handlePairIdOrderByNode[id];
    delete state.labwareSlotMappingByTask[id];
    delete state.nodeIdToColorIndex[id];
  }

  // delete connected edges
  const connectedEdgeIds = getConnectedEdgeIdsForNodes(
    [...labwareIds, ...taskIds],
    [...state.labwareDependencyEdges, ...state.dataDependencyEdges]
  );
  for (const id of connectedEdgeIds) {
    deleteEdgeHelper(state, id);
  }

  // delete connected task dependencies
  const connectedTaskDependencies = getConnectedTaskDependencyIdsForNodes(
    taskIds,
    state.taskDependencies
  );
  for (const id of connectedTaskDependencies) {
    delete state.taskDependencies[id];
  }

  // delete connected time constraints
  const connectedTimeConstraintIds = getConnectedTimeConstraintIdsForNodes(
    taskIds,
    state.timeConstraints
  );
  for (const id of connectedTimeConstraintIds) {
    delete state.timeConstraints[id];
  }

  // remove any deleted nodes & edges from selection
  state.selectedNodes = state.selectedNodes.filter(
    (nodeId) => !labwareIds.includes(nodeId) && !taskIds.includes(nodeId)
  );
  state.selectedEdges = state.selectedEdges.filter(
    (edgeId) => !connectedEdgeIds.includes(edgeId)
  );
}

export function createLabwareHelper(
  state: GraphState,
  labware: CreateLabwareData
): void {
  const { colorIndex, position } = labware;
  const { id } = labware.properties;

  state.labwareNodes[id] = position;

  if (colorIndex !== undefined) {
    state.nodeIdToColorIndex[id] = colorIndex;
  }
}

export function deselectAllNodesEdgesHelper(state: GraphState): void {
  state.selectedEdges = [];
  state.selectedNodes = [];
}

export function createTaskHelper(
  state: GraphState,
  task: CreateTaskData
): void {
  const { position, handlePairIdOrder } = task;
  const id = task.properties.id;

  state.taskNodes[id] = position;

  if (handlePairIdOrder !== undefined) {
    state.handlePairIdOrderByNode[id] = handlePairIdOrder;
  }
}

/** represent a time constraint connection as a deterministic string */
export const _hashTimeConstraintConnection = ({
  source,
  sourceType,
  target,
  targetType,
}: TimeConstraintInstance): string => {
  const edgeHandleA = `${source}.${sourceType}`;
  const edgeHandleB = `${target}.${targetType}`;
  const alphaOrder: [string, string] = [edgeHandleA, edgeHandleB];
  alphaOrder.sort();
  return `${alphaOrder[0]},${alphaOrder[1]}`;
};

export const _getHashedTimeConstraintsById = (
  timeConstraints: TimeConstraintInstancesById
): string[] => {
  return Object.values(timeConstraints).map(_hashTimeConstraintConnection);
};

export const getHashedTimeConstraintsByIdMemoized = memoize(
  _getHashedTimeConstraintsById
);

export const timeConstraintConnectionExists = (
  allTimeConstraintHashes: string[],
  timeConstraintInstance: TimeConstraintInstance
): boolean => {
  return allTimeConstraintHashes.includes(
    _hashTimeConstraintConnection(timeConstraintInstance)
  );
};

export const createGraphologyGraph = (
  labwareNodes: NodePositionById,
  taskNodes: NodePositionById,
  edges: {
    source: string;
    target: string;
  }[],
  taskProperties: Record<string, { nickname: string }> = {},
  labwareProperties: Record<string, { nickname: string }> = {}
) => {
  // TODO: generic types? SEE: https://graphology.github.io/instantiation.html#generics
  const graph = new Graph<{ nickname: string | undefined }, {}, {}>({
    multi: false,
    type: 'directed',
    allowSelfLoops: false,
  });
  for (const id in labwareNodes) {
    graph.addNode(id, {
      nickname: labwareProperties[id]?.nickname,
    });
  }
  for (const id in taskNodes) {
    graph.addNode(id, {
      nickname: taskProperties[id]?.nickname,
    });
  }

  /*
   * We can legitimately have duplicate edges - e.g. if two data outputs from a task are both inputs to another task
   * - so we need to deduplicate them before adding to the graph, which we use for circular dependency detection
   * (and so duplicate edges don't matter). One risk of this is that if ever our use of this graph changes this
   * assumption may no longer hold true, but for now it's a reasonable trade-off.
   */
  const uniqueEdges = uniqWith(
    edges,
    (e1, e2) => e1.source === e2.source && e1.target === e2.target
  );

  for (const { source, target } of uniqueEdges) {
    graph.addEdge(source, target);
  }

  return graph;
};

export const graphologyGraph = memoize(createGraphologyGraph);

export const isTargetNodeDependantOnSourceNode = (
  graph: Graph,
  sourceNode: string,
  targetNode: string
): boolean => {
  let found = false;
  dfsFromNode(graph, sourceNode, (node) => {
    if (node === targetNode) {
      found = true;
      return true;
    }
  });
  return found;
};

/** Flip the source/target attributes of a connection */
export const reverseConnection = (rawConnection: Connection): Connection => {
  return {
    source: rawConnection.target,
    sourceHandle: rawConnection.targetHandle,
    target: rawConnection.source,
    targetHandle: rawConnection.sourceHandle,
  };
};

const _reverseTimeConstraint = (
  timeConstraint: TimeConstraintInstance
): TimeConstraintInstance => ({
  ...timeConstraint,
  source: timeConstraint.target,
  sourceType: timeConstraint.targetType,
  target: timeConstraint.source,
  targetType: timeConstraint.sourceType,
});

export const rectifyTimeConstraintDirection = (
  graph: Graph,
  timeConstraint: TimeConstraintInstance
): TimeConstraintInstance => {
  // empty graph
  if (graph.order === 0) {
    return timeConstraint;
  }
  const needsReversal = !isTargetNodeDependantOnSourceNode(
    graph,
    timeConstraint.source,
    timeConstraint.target
  );
  if (needsReversal) {
    return _reverseTimeConstraint(timeConstraint);
  }
  return timeConstraint;
};

export const rectifyTimeConstraints = (
  taskNodes: NodePositionById,
  labwareNodes: NodePositionById,
  timeConstraints: TimeConstraintInstancesById,
  labwareDependencyEdges: GraphEdges
): TimeConstraintInstancesById | undefined => {
  if (
    Object.keys(timeConstraints).length === 0 ||
    Object.keys(labwareNodes).length === 0 ||
    Object.keys(taskNodes).length === 0 ||
    labwareDependencyEdges.length === 0
  ) {
    return;
  }
  const graph = graphologyGraph(
    labwareNodes,
    taskNodes,
    labwareDependencyEdges
  );

  return Object.entries(timeConstraints).reduce(
    (prev, [key, timeConstraint]) => {
      return {
        ...prev,
        [key]: rectifyTimeConstraintDirection(graph, timeConstraint),
      };
    },
    {}
  );
};

export const deleteAssociatedTaskDependencies = (
  graphState: GraphState,
  edgeAndTaskDependencyId: string
): void => {
  graphState.taskDependencies = Object.fromEntries(
    Object.entries(graphState.taskDependencies).filter(
      ([key]) => !edgeAndTaskDependencyId.includes(key)
    )
  );
};
